import { HttpClient } from "services/HttpClient";
import {
  Exam,
  ExamItem,
  Note,
  PatchNoteBody,
  PatchNoteParams,
  PostNoteParams,
} from "types/Exam";
import { ApiPaginatedResponse, CommonApiParams } from "../types/common";
import { getData } from "../contexts/authContext";
import * as cognito from "../libs/cognito";

export class ExamRepository extends HttpClient {
  // eslint-disable-next-line class-methods-use-this
  public getExam = async (id: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.get<{ exam: Exam }>(`/exam/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  };

  // eslint-disable-next-line class-methods-use-this
  public getExams = async (
    patientId: string,
    params: CommonApiParams & { freeForm?: boolean }
  ) => {
    const token = await getData("idToken");

    const { data } = await this.instance.get<ApiPaginatedResponse<ExamItem>>(
      `/exam?patientId=${patientId}`,
      {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  };

  // eslint-disable-next-line class-methods-use-this
  public getExamGroup = async (examGroupId: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.get<ApiPaginatedResponse<Exam>>(
      `/exam/examGroupId?examGroupId=${examGroupId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data.items;
  };

  // eslint-disable-next-line class-methods-use-this
  public postNote = async ({
    patientRegionId,
    examId,
    note,
  }: PostNoteParams) => {
    const token = await getData("idToken");

    const { data } = await this.instance.post<Note, any, PatchNoteBody>(
      `/exam/note`,
      {
        patientRegionId,
        examId,
        note: { title: note },
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  // eslint-disable-next-line class-methods-use-this
  public patchNote = async ({
    patientRegionId,
    examId,
    noteId,
    note,
  }: PatchNoteParams) => {
    const token = await getData("idToken");

    const { data } = await this.instance.patch<Note, any, PatchNoteBody>(
      `/exam/note/${noteId}`,
      {
        patientRegionId,
        examId,
        note: { title: note },
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  // eslint-disable-next-line class-methods-use-this
  public deleteNote = async ({
    patientRegionId,
    examId,
    noteId,
  }: Omit<PatchNoteParams, "note">) => {
    const token = await getData("idToken");

    const { data } = await this.instance.delete<Note>(`/exam/note/${noteId}`, {
      data: {
        patientRegionId,
        examId,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  };

  public getExamFrames = async (examId: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.get<any>(
      `/exam-ml/${examId}/ml-result`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  public getMinAndMaxExamFrames = async (examId: string) => {
    const token = await getData("idToken");

    const { data } = await this.instance.get<any>(
      `/exam-ml/${examId}/min-and-max-frames`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return data;
  };

  constructor() {
    super(process.env.REACT_APP_BASE_URL as string, () => {
      cognito.signOut(true);
    });
  }

  private static classInstance?: ExamRepository;

  public static getInstance() {
    if (!this.classInstance) {
      this.classInstance = new ExamRepository();
    }
    return this.classInstance;
  }
}
